/** Unambiguous alphabet: no I, O, 0 or 1. Length 32, so a random byte is unbiased. */
export const ROOM_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

const ROOM_CODE = /^[A-HJ-NP-Z2-9]{4}$/;

export function roomCodeFromBytes(bytes: Uint8Array): string {
  let code = '';
  for (let i = 0; i < 4; i += 1) {
    const value = bytes[i] ?? 0;
    code += ROOM_ALPHABET[value % ROOM_ALPHABET.length] ?? 'A';
  }
  return code;
}

export function randomRoomCode(): string {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  return roomCodeFromBytes(bytes);
}

export function randomSeed(): number {
  const words = new Uint32Array(1);
  crypto.getRandomValues(words);
  return (words[0] ?? 1) % 1_000_000_000;
}

export function normalizeRoomCode(value: string): string | null {
  const code = value.trim().toUpperCase();
  return ROOM_CODE.test(code) ? code : null;
}

/** `?room=ABCD` from a shared link. */
export function roomCodeFromSearch(search: string): string | null {
  const code = new URLSearchParams(search).get('room');
  if (!code) return null;
  return normalizeRoomCode(code);
}

/** Share or store a room link without leftover Auth hashes. */
export function roomLink(href: string, code: string): string {
  const url = new URL(href);
  url.search = `?room=${code}`;
  url.hash = '';
  return url.toString();
}

/**
 * Supabase writes failed email-link sign-in as `#error=...&error_code=otp_expired`.
 * That hash must not stay on a room URL or Auth will keep retrying it.
 */
export function authRedirectError(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  const code = params.get('error_code') ?? params.get('error');
  if (!code) return null;
  if (code === 'otp_expired') return 'That email link expired. Sign in with your password.';
  if (code === 'access_denied') return 'That email link was denied. Sign in with your password.';
  return 'That email link did not work. Sign in with your password.';
}

const NAME = /^[A-Za-z0-9 _-]{2,16}$/;

export function sanitizeDisplayName(value: string): string | null {
  const name = value.trim().replace(/\s+/g, ' ');
  return NAME.test(name) ? name : null;
}

export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}
